// images
import iphoneMock from "../../images/StartConsultation/iphone_mock.png";
import videoCall from "../../images/StartConsultation/video_call.png";

// components
import ProcessFlow from '../../components/ProcessFlow/ProcessFlow';

// utils
import { getPrice } from '../../utils/translator'
import { translate } from '../../utils/translator';

// trackers
import { trackOncePerSession } from '../../PixelTrackers/trackingUtils';
import { useSearchParams } from 'react-router-dom';
import { CANNALEO, FLOWZZ, FLOWZZ_PREMIUM } from "../../utils/consts";


type Props = {
    selectedConsultationType: "video" | "text";
    setSelectedConsultationType: (consultationType: "video" | "text") => void;
    treatmentFees: {text: number, video: number, cannabisNetwork: number};
    language: string;
    country: string;
    isTextDisabled?: boolean;
    mention?: string;
}

const ConsultationTypeSelection: React.FC<Props> = ({selectedConsultationType, setSelectedConsultationType, treatmentFees, country, language, isTextDisabled = false, mention = ''}) => {
    const [searchParams] = useSearchParams();
    const cameFromCannabisNetwork = searchParams.get("af") === FLOWZZ || searchParams.get("af") === CANNALEO || searchParams.get("af") === FLOWZZ_PREMIUM;
     
    const getProcessFlow = (consultationType: "video" | "text") => {
        const treatmentFee = cameFromCannabisNetwork && consultationType !== "video" ? treatmentFees["cannabisNetwork"] : treatmentFees[consultationType];
        return (
            <div 
                id="consultation-type-card-process-flow"
                className={`
                    select-none 
                    md:cursor-pointer
                    col-span-2
                    md:row-start-2
                    md:row-end-3
                    md:px-8 px-[18px]
                    md:pt-[20px]
                    md:pb-[20px]
                    transition-all duration-[400ms] ease-in-out
                    md:block
                    bg-[#ffffff]
                    md:rounded-bl-[16px]
                    md:rounded-br-[16px]
                    z-[2]
                    relative

                    md:before:hidden
                    before:content-[''] 
                    before:absolute 
                    before:top-[-14px] 
                    before:left-[0px] 
                    before:w-full 
                    before:h-[14px] 
                    before:bg-inherit 
                    before:rounded-t-[16px]

                    ${selectedConsultationType === consultationType ? "md:bg-[#ffffff]" : "md:bg-[#f5f7f5] hidden"}
                    ${consultationType === "text" ? "md:col-start-1 md:col-end-2" : "md:col-start-2 md:col-end-3"}
                    ${consultationType === "text" && isTextDisabled ? "opacity-50 !cursor-not-allowed pointer-events-none" : ""}
                `}
                onClick={() => {
                    if (consultationType === "text" && isTextDisabled) return;
                    setSelectedConsultationType(consultationType)
                    if (consultationType === "video") {
                        trackOncePerSession("custom", "Videoconsultation selected")
                    }
                }}
            >
                {consultationType === 'video' && mention === 'erectile_dysfunction' && (
                    <p className="md:hidden block text-[12px] text-gray-500 pt-[10px]">
                        {translate(language, "start_consultation", "processFlow/video_footnote")}
                    </p>
                )}
                {/* consultation type card image */}
                <div
                    id="consultation-type-process-flow-title-and-price-mobile"
                    className="
                        md:hidden
                        flex
                        items-start
                        justify-between
                        pb-[20px] pt-[10px]
                        gap-x-[30px]
                    "
                >
                    <h3
                        id="consultation-type-process-flow-title-mobile"
                        className="
                            text-[16px]
                            font-medium
                            text-black
                        "
                    >
                        {translate(language, "start_consultation", `processFlow/${consultationType}_title_mobile`)}
                    </h3>
                
                    <div 
                        id="consultation-type-card-price-mobile"
                        className="
                            flex
                            items-center
                            justify-center
                            w-fit
                            px-[8px]
                            py-[2px]
                            text-white
                            bg-[linear-gradient(to_right,#209972,#55A950)]
                            rounded-[20px]
                            font-medium
                            text-[17px]
                            leading-[1.3]
                            italic
                        "
                    >
                        {getPrice(treatmentFee, `${language}-${country.toUpperCase()}`)}
                    </div>
                </div>

                {/* process flow */}
                {/* @ts-ignore */}
                <ProcessFlow 
                    consultationType={consultationType}
                    language={language}
                />
            </div>
        )
    }

    const getHeader = (consultationType: "video" | "text") => {
        const treatmentFee = cameFromCannabisNetwork && consultationType !== "video" ? treatmentFees["cannabisNetwork"] : treatmentFees[consultationType];
        return (
            <div 
                id="consultation-type-card-header"
                className={`
                    relative
                    z-[1]
                    select-none 
                    cursor-pointer
                    grid
                    md:[direction:ltr]
                    [direction:rtl] 
                    row-start-1
                    row-end-2
                    md:grid-cols-[auto_minmax(0,1fr)]
                    grid-cols-[minmax(0,1fr)_auto]
                    xl:gap-[30px] md:gap-[15px] gap-[10px]
                    xl:p-5 md:p-3 p-[10px]
                    md:pb-0
                    rounded-tl-[16px]
                    rounded-tr-[16px]
                    md:items-start
                    items-center
                    self-end md:self-auto
                    transition-all duration-[400ms] ease-in-out
                    pb-[24px]

                    md:after:opacity-0
                    after:content-[''] after:absolute after:top-0 after:bottom-0
                    after:transition-all after:duration-[400ms] after:ease-in-out  after:bg-[#F5F7F5] after:rounded-t-[16px] after:z-[3] after:border-[2px] after:border-[#ffffff] after:border-solid

                    before:rounded-t-[16px]
                    md:before:opacity-0
                    before:content-[''] before:absolute before:top-0 before:bottom-0 before:z-[4]
                    before:bg-[linear-gradient(to_right,#279B6E,#54A951)]
                    before:transition-all before:duration-[400ms] before:ease-in-out
                   
                    ${selectedConsultationType === consultationType
                        ? 
                            `
                                bg-[#F5F7F5] 
                                z-[2] 
                                md:bg-[#D9E9D5] 
                                before:opacity-100 
                                before:w-[100%]
                                after:w-[100%]
                                after:opacity-0 
                            `
                                : 
                            `
                                bg-[#F5F7F5] 
                                md:bg-[#EAEEE9] 
                                before:top-[2px]
                                before:opacity-0 
                                before:w-[calc(120%-4px)]
                                after:w-[120%]
                                after:opacity-100
                            ` 
                    }

                    ${consultationType === `text` 
                        ? 
                            `
                                col-start-1 
                                col-end-2
                                after:left-0
                                ${selectedConsultationType !== consultationType ? "before:left-[2px]" : "before:left-[0px]"}

                            ` 
                        : 
                            `
                                col-start-2 
                                col-end-3
                                after:right-0
                                ${selectedConsultationType !== consultationType ? "before:right-[2px]" : "before:right-[0px]"}
                            ` 
                    }
                    ${consultationType === "text" && isTextDisabled ? "opacity-50 !cursor-not-allowed pointer-events-none" : ""}
                `}
                onClick={() => {
                    if (consultationType === "text" && isTextDisabled) return;
                    setSelectedConsultationType(consultationType);
                }}
            >
                <div
                    id="consultation-type-card-image-container"
                    className={`
                        md:flex
                        hidden
                        xl:w-[120px]
                        xl:max-h-[124px]
                        md:w-[100%]
                        md:max-h-[100%]
                        overflow-hidden
                        xl:self-start
                        md:self-end
                        ${consultationType === "text" ? "md:max-w-[100px]" : ""}
                    `}
                >
                    <img
                        src={consultationType === "text" ? iphoneMock : videoCall} 
                        alt="" 
                        className={`
                            w-full
                            object-cover
                            max-w-[100%]
                            ${consultationType === "text" ? "self-start" : "self-end"}
                        `}
                    />
                </div>

                <div
                    id="consultation-type-card-title-and-price"
                    className="
                        relative
                        z-[4]
                        md:self-stretch md:flex md:flex-col
                    "
                >
                    <h2
                        id="consultation-type-card-title"
                        className={`
                            md:font-medium
                            xl:text-[30px]
                            md:text-[clamp(10px,1.8vw,25px)]
                            md:line-height: 2.1
                            line-height: 1.2
                            md:text-black
                            md:normal-case
                            uppercase
                            xl:mb-3
                            md:mb-2
                            transition-all duration-[400ms] ease-in-out
                            md:text-start text-end
                            whitespace-nowrap
                            ${selectedConsultationType === consultationType ? "text-[clamp(8px,2.7vw,16px)] text-white font-semibold" : "text-[clamp(8px,2.7vw,16px)] font-normal"}
                        `}
                    >
                        <>
                            <span className="inline">{translate(language, "start_consultation", `processFlow/${consultationType}_cta_segment_1`)}</span>
                            {' '}
                            <span className="inline">{translate(language, "start_consultation", `processFlow/${consultationType}_cta_segment_2`)}</span>
                        </>
                    </h2>

                    <div 
                        id={`consultation-type-card-price-${consultationType}`}
                        className="
                            md:flex
                            hidden
                            items-center
                            justify-center
                            w-fit
                            px-[9px]
                            text-white
                            bg-[linear-gradient(to_right,#10947D,#91BC28)]
                            rounded-[10px]
                            font-medium
                            xl:text-[28px]
                            md:text-[24px]
                            leading-[1.3]
                            italic
                        "
                    >
                        {getPrice(treatmentFee, `${language}-${country.toUpperCase()}`)}
                    </div>
                    {consultationType === 'video' && mention === 'erectile_dysfunction' && (
                        <p className="md:block hidden text-[12px] text-gray-500 md:mt-auto">
                            {translate(language, "start_consultation", "processFlow/video_footnote")}
                        </p>
                    )}
                </div>

                {/* button */}
                <div 
                    id="consultation-type-card-button"
                    className={`
                        xl:h-[36px]
                        xl:w-[36px]
                        md:h-[28px]
                        md:w-[28px]
                        h-[21px]
                        w-[21px]
                        bg-white
                        rounded-[50%]
                        md:absolute
                        relative
                        md:top-[20px]
                        md:right-[20px]
                        justify-self-end
                        border-[2px]
                        border-[#2B9C6C]
                        border-solid
                        transition-all duration-[400ms] ease-in-out
                        z-[4]
                        ${selectedConsultationType === consultationType ? "border-transparent" : ""}
                    `}
                >
                    {/* selected-inner-circle */}
                    <div 
                        id="consultation-type-card-button-selected-inner-circle"
                        className={`
                            absolute
                            top-1/2
                            left-1/2
                            md:w-[70%] md:h-[70%]
                            w-[80%] h-[80%]
                            rounded-full
                            [transform:translate(-50%,-50%)]
                            bg-[#2B9C6C]
                            shadow-[0_5.14px_11.14px_0_rgba(42,156,109,0.6196)]
                            transition-all duration-[400ms] ease-in-out
                            ${selectedConsultationType === consultationType ? "opacity-100" : "opacity-0"}
                        `}
                    >
                    </div>
                </div>
            </div>
        )
    }

    const getOutline = (consultationType: "video" | "text") => {
        return (
            <div 
                id={`consultation-type-selection-container-outline-${consultationType}`}
                className={`
                    hidden
                    md:block
                    pointer-events-none
                    relative
                    row-start-1
                    row-end-[-1]
                    rounded-[16px]
                    border-[3px] 
                    border-[#ffffff] 
                    border-solid
                    z-[2]
                    ${consultationType === "text" ? "col-start-1 col-end-2" : "col-start-2 col-end-3"}
                `}
            >
            </div>
        )
    }

    return (
        <section 
            id="consultation-type-selection-container" 
            className="
                w-full
                select-none 
                grid
                md:gap-x-[30px]
                md:grid-cols-2
                md:grid-rows-[minmax(0,_144px)_1fr] grid-rows-[auto_1fr]
                font-poppins"
        >
            {/* text consultation type header */}
            {getHeader("text")}

            {/* video consultation type header */}
            {getHeader("video")}

            {/* text consultation type process flow */} 
            {getProcessFlow("text")}

            {/* video consultation type process flow */}
            {getProcessFlow("video")}

            {/* outlines / borders speratly for mobile compatibility */}
            {getOutline("text")}

            {getOutline("video")}
        </section>
    )
}

export default ConsultationTypeSelection;